import { useState } from 'react';
import { X, Send, Info, ShoppingCart, TrendingUp } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../lib/api';

const InquiryModal = ({ isOpen, onClose, ideaId, ideaTitle, inquiryType }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    budget: '',
  });

  const typeConfig = {
    info: {
      title: 'Request Information',
      icon: Info,
      color: 'text-cyan-400',
      buttonColor: 'bg-cyan-500 hover:bg-cyan-400',
    },
    buy: {
      title: 'Express Interest to Buy',
      icon: ShoppingCart,
      color: 'text-violet-400',
      buttonColor: 'bg-violet-500 hover:bg-violet-400',
    },
    invest: {
      title: 'Invest in Development',
      icon: TrendingUp,
      color: 'text-emerald-400',
      buttonColor: 'bg-emerald-500 hover:bg-emerald-400',
    },
  };

  const config = typeConfig[inquiryType] || typeConfig.info;
  const Icon = config.icon;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/inquiries', {
        idea_id: ideaId,
        inquiry_type: inquiryType,
        ...formData,
      });

      toast.success('Inquiry submitted successfully! We\'ll be in touch soon.');
      setFormData({ name: '', email: '', company: '', message: '', budget: '' });
      onClose();
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border border-white/10 text-white max-w-md" data-testid="inquiry-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 font-heading">
            <Icon className={`h-5 w-5 ${config.color}`} />
            <span>{config.title}</span>
          </DialogTitle>
        </DialogHeader>

        <p className="text-slate-400 text-sm mb-4">
          Regarding: <span className="text-white font-medium">{ideaTitle}</span>
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name" className="text-slate-300">Full Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="bg-slate-950/50 border-white/10 focus:border-cyan-500/50 text-white"
              placeholder="John Doe"
              data-testid="inquiry-name-input"
            />
          </div>

          <div>
            <Label htmlFor="email" className="text-slate-300">Email Address *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="bg-slate-950/50 border-white/10 focus:border-cyan-500/50 text-white"
              placeholder="john@company.com"
              data-testid="inquiry-email-input"
            />
          </div>

          <div>
            <Label htmlFor="company" className="text-slate-300">Company (Optional)</Label>
            <Input
              id="company"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              className="bg-slate-950/50 border-white/10 focus:border-cyan-500/50 text-white"
              placeholder="Acme Inc."
              data-testid="inquiry-company-input"
            />
          </div>

          {(inquiryType === 'buy' || inquiryType === 'invest') && (
            <div>
              <Label htmlFor="budget" className="text-slate-300">
                {inquiryType === 'buy' ? 'Budget Range' : 'Investment Amount'}
              </Label>
              <Input
                id="budget"
                value={formData.budget}
                onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                className="bg-slate-950/50 border-white/10 focus:border-cyan-500/50 text-white"
                placeholder={inquiryType === 'buy' ? '$10,000 - $50,000' : '$25,000'}
                data-testid="inquiry-budget-input"
              />
            </div>
          )}

          <div>
            <Label htmlFor="message" className="text-slate-300">Message *</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              required
              rows={4}
              className="bg-slate-950/50 border-white/10 focus:border-cyan-500/50 text-white resize-none"
              placeholder="Tell us more about your interest..."
              data-testid="inquiry-message-input"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="flex-1 border border-white/10 text-slate-300 hover:bg-white/5"
              data-testid="inquiry-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className={`flex-1 ${config.buttonColor} text-black font-semibold`}
              data-testid="inquiry-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Sending...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Send className="h-4 w-4" />
                  Submit
                </span>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default InquiryModal;
